import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as transfersApi from './transfers.api';

import type {
  CreateTransferRequest,
  TransferApprovalRequest,
  TransferListQuery,
  TransferPhoto,
  UpdateTransferRequest,
} from '@utazzo/shared';

const QK = {
  list: (query: TransferListQuery) => ['transfers', 'list', query] as const,
  detail: (id: string) => ['transfers', 'detail', id] as const,
};

export const useTransfersList = (query: TransferListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => transfersApi.listTransfers(query),
    staleTime: 30_000,
  });

/**
 * Facets (in-use countries + cities) for the Country / City list filters.
 * Keyed under ['transfers'] so create/update/delete invalidations refresh it -
 * a transfer added for a new city shows up in the filter automatically.
 */
export const useTransferFacets = () =>
  useQuery({
    queryKey: ['transfers', 'facets'] as const,
    queryFn: transfersApi.getTransferFacets,
    staleTime: 60_000,
  });

export const useTransferDetail = (id: string | null | undefined) =>
  useQuery({
    queryKey: QK.detail(id ?? ''),
    queryFn: () => transfersApi.getTransfer(id as string),
    staleTime: 30_000,
    enabled: Boolean(id),
  });

export const useCreateTransfer = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateTransferRequest) => transfersApi.createTransfer(payload),
    onSuccess: (transfer) => {
      // Broad key so the list AND the filter facets refresh (a new city/country
      // used by this transfer should appear in the filters right away).
      qc.invalidateQueries({ queryKey: ['transfers'] });
      toast.success(`Added ${transfer.name}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add transfer'),
  });
};

export const useUpdateTransfer = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateTransferRequest }) =>
      transfersApi.updateTransfer(id, payload),
    onSuccess: (transfer) => {
      qc.invalidateQueries({ queryKey: ['transfers'] });
      qc.invalidateQueries({ queryKey: QK.detail(transfer.id) });
      // After a transfer's first approval a non-admin's edit is captured into
      // pendingPayload and the live record is untouched - so "Transfer
      // updated" would be a lie. Say where the change actually went.
      if (transfer.awaitingApproval) {
        qc.invalidateQueries({ queryKey: ['catalog-approvals'] });
        toast.success('Sent to admin for approval', {
          description:
            'Your changes are saved but stay off the live record until an admin approves.',
        });
      } else {
        toast.success('Transfer updated');
      }
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update transfer'),
  });
};

export const useApproveTransfer = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: TransferApprovalRequest }) =>
      transfersApi.approveTransfer(id, payload),
    onSuccess: (transfer, vars) => {
      qc.invalidateQueries({ queryKey: ['transfers', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(transfer.id) });
      toast.success(
        `${vars.payload.tier === 'admin' ? 'Admin' : 'Moderator'} ${vars.payload.approve ? 'approved' : 'approval revoked'}`,
      );
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to change approval'),
  });
};

export const useDeleteTransfer = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => transfersApi.deleteTransfer(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transfers'] });
      toast.success('Transfer deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete transfer'),
  });
};

export const useDuplicateTransfer = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => transfersApi.duplicateTransfer(id),
    onSuccess: (transfer) => {
      qc.invalidateQueries({ queryKey: ['transfers'] });
      toast.success(`Duplicated as "${transfer.name}" (draft)`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to duplicate transfer'),
  });
};

export const useBulkDeleteTransfers = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => transfersApi.bulkDeleteTransfers(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['transfers'] }),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete transfers'),
  });
};

/**
 * Bulk-apply a cover + gallery to many transfers (Transfers -> Media tool).
 * Overwrites the cover/gallery on every selected transfer with the same set.
 */
export const useBulkApplyTransferMedia = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      ids: string[];
      photos: TransferPhoto[] | null;
      galleryImageMediaIds: string[];
    }) =>
      transfersApi.bulkApplyTransferMedia(vars.ids, {
        photos: vars.photos,
        galleryImageMediaIds: vars.galleryImageMediaIds,
      }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['transfers'] });
      if (res.failed.length > 0) {
        toast.warning(`Applied to ${res.updated}; ${res.failed.length} failed`);
      } else {
        toast.success(`Images applied to ${res.updated} transfer${res.updated === 1 ? '' : 's'}`);
      }
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to apply media'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
